// CONFIRMACIÓN CON EL DISEÑO DE LA CASA — reemplazo de `window.confirm()`.
//
// Por qué existe (Carlos, 7-ago-2026): el `confirm()` del navegador saca el cuadro gris del
// sistema operativo encima de la consola. Rompe el diseño, no admite formato ni jerarquía, y en
// móvil aparece pegado arriba con tipografía ajena. Una acción delicada merece leerse bien.
//
// Mismo patrón de modal que PaymentForm (createPortal + overlay + panel), para que se sienta
// igual que el resto: entra por abajo en móvil, centrado en escritorio.
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';

interface Props {
  title: string;
  /** Cuerpo. Varios párrafos = varias entradas del array. */
  lines: string[];
  /** Aviso destacado en ámbar: la consecuencia que no se puede pasar por alto. */
  warning?: string;
  confirmLabel: string;
  cancelLabel?: string;
  /** 'danger' pinta el botón en rojo. Para lo que apaga o borra algo. */
  tone?: 'danger' | 'normal';
  onConfirm: () => void;
  onClose: () => void;
}

export default function ConfirmDialog({
  title, lines, warning, confirmLabel, cancelLabel = 'Cancelar', tone = 'danger', onConfirm, onClose,
}: Props) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Escape cierra, y el foco arranca en el botón de confirmar para poder resolver con el teclado.
  // Ese foco NO es un riesgo: un Enter suelto no dispara nada porque el diálogo se abre por clic.
  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="alertdialog" aria-modal="true" aria-label={title}>
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative w-full max-w-md rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-content">
            {tone === 'danger' && <AlertTriangle size={18} className="shrink-0 text-amber-500" />}
            {title}
          </h3>
          <button onClick={onClose} className="shrink-0 text-content-3 transition hover:text-content" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-2 text-sm text-content-2">
          {lines.map((l, i) => <p key={i}>{l}</p>)}
        </div>

        {warning && (
          <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-content-2">
            {warning}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose}
            className="rounded-lg border border-line bg-surface px-3.5 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2">
            {cancelLabel}
          </button>
          <button ref={confirmRef} onClick={() => { onConfirm(); onClose(); }}
            className={`rounded-lg px-3.5 py-2 text-sm font-semibold text-white transition ${
              tone === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-accent-solid text-on-accent hover:bg-accent-solid/90'}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>, document.body);
}
